import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the 3-2-1 Tuesday Ballpark Pass?",
    answer: (
      <>
        The 3-2-1 Tuesday Ballpark Pass gives you the ability to attend all 12
        regular season Tuesday home games throughout the 2025 season. The ticket
        will come as a Standing Room Only ticket. At all Tuesday Reds home games
        fans can buy <b>$3</b> beers (select 12-oz cans), <b>$2 hot dogs</b> and{" "}
        <b>$1 ice cream cups</b>.
      </>
    ),
  },
  {
    question: "When is the check-in window?",
    answer:
      "The check-in window opens at 9am the day of the game. You will be notified via text when the period is open.",
  },
  {
    question: "Does the 3-2-1 Tuesday Ballpark Pass include an assigned seat?",
    answer:
      "No. The pass will not include an assigned seat. Pass holders do have the ability to upgrade to an assigned seat at an additional cost, subject to availability.",
  },
  {
    question: "Where can I go with a Standing Room Only Ticket?",
    answer:
      "With a Standing Room Only Ticket, you can enjoy the game from designated standing areas throughout Great American Ball Park, including concourses down the first and third base line, our Budweiser Bowtie Bar located in right field, and at the top of section 145 underneath the Power Stacks.",
  },
  {
    question: "How do I upgrade my seats?",
    answer:
      "After confirming your attendance on the day of the game, you'll have the opportunity to purchase upgraded tickets. After confirming attendance simply select the “Add-Ons” option below your game to see all available upgrades. Subject to availability.",
  },
  {
    question: "Where are my tickets delivered?",
    answer:
      "All of your tickets will be delivered digitally via the MLB Ballpark app.",
  },
  {
    question: "When will my tickets appear in my MLB Ballpark App?",
    answer:
      "Tickets will be delivered to your MLB Ballpark App account 3 hours prior to first pitch.",
  },
  {
    question: "Are there any penalties if I do not check-in?",
    answer:
      "There are no penalties for not attending a game, however you do need to confirm your attendance (check-in) to receive a ticket.",
  },
  {
    question:
      "Can 3-2-1 Tuesday Ballpark Pass tickets be transferred to a friend?",
    answer:
      "Passes cannot be shared/forwarded to other parties. Admission to Great American Ball Park will be managed through the MLB Ballpark app on your mobile device. Multiple packages can be purchased if you and the rest of your party enter at the same time.",
  },
  {
    question:
      "Can I sell my 3-2-1 Tuesday Ballpark Pass for games that I do not attend?",
    answer:
      "No, 3-2-1 Tuesday Ballpark Passes are non-transferable and are only available on the phone of the 3-2-1 Tuesday Ballpark Pass purchaser. 3-2-1 Tuesday Ballpark Passes cannot be resold. Violators will be removed from the program.",
  },
  {
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your 3-2-1 Tuesday Ballpark Pass ticket will be valid for the rescheduled game only.",
  },
  {
    question:
      "Will I be considered a Season Ticket Member? Do I have access to Season Ticket Member events?",
    answer:
      "No, 3-2-1 Tuesday Ballpark Pass holders are not eligible for Season Ticket Member benefits. Tickets are not transferable and cannot be exchanged for a later date.",
  },
  {
    question: "Does the 3-2-1 Tuesday Ballpark Pass include postseason access?",
    answer:
      "No, the 3-2-1 Tuesday Ballpark Pass is not considered a season plan and does not guarantee any postseason access.",
  },
  {
    question: "Is there any loaded concession value on a Ballpark Pass ticket?",
    answer:
      "No. Each 3-2-1 Tuesday Ballpark Pass ticket will NOT include any loaded concession value.",
  },
  {
    question:
      "Are any other events included with the 3-2-1 Tuesday Ballpark Pass?",
    answer:
      "The 3-2-1 Tuesday Ballpark Pass is only valid for regular-season home games. It does not include tickets to non-game events.",
  },
  {
    question: "How can I get a refund and cancel my 3-2-1 Tuesday pass?",
    answer: (
      <>
        By selecting the Place Order button, you agree to purchase the tickets
        you have selected. There are no refunds or exchanges. Please visit
        General TicketingTerms & Conditions | Cincinnati Reds for the full
        ticket terms and conditions.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    <>
      By checking this box you agree to receive SMS alerts regarding ticket
      delivery and agree to the{" "}
      <Link target="_blank" href="https://www.attend.tech/terms-of-use">
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link target="_blank" href="https://www.attend.tech/privacy-policy">
        Privacy Policy
      </Link>
      .
    </>
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/reds/tickets/info/terms-conditions?partnerId=redirect-cin-terms"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General TicketingTerms & Conditions | Cincinnati Reds
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };
